/**
 * ChatHistory — lists past conversations for the AI chat.
 * Features: search, date grouping, pinned conversations, markdown-stripped previews.
 */

import { memo, useState, useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { cn } from "@/shared/utils/general/cn"
import type { DbChatConversation } from "@totodo/operations"

interface ChatHistoryProps {
  onSelect: (conversationId: string) => void
  activeId: string | null
}

export const ChatHistory = memo(function ChatHistory({
  onSelect,
  activeId,
}: ChatHistoryProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")

  const [conversations] = useLiveQueryImmediate(
    () =>
      db.chatConversation
        .orderBy("updatedAt")
        .reverse()
        .toArray(),
    [],
    undefined,
  )

  const filtered = useMemo(() => {
    if (!conversations) return []
    if (!search.trim()) return conversations
    const q = search.toLowerCase()
    return conversations.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.preview?.toLowerCase().includes(q) ||
        c.summaryTitle?.toLowerCase().includes(q) ||
        c.category?.toLowerCase().includes(q),
    )
  }, [conversations, search])

  const grouped = useMemo(() => groupByDate(filtered), [filtered])

  const togglePin = useCallback(
    async (e: React.MouseEvent, conv: DbChatConversation) => {
      e.stopPropagation()
      if (!conv.id) return
      await db.chatConversation.update(conv.id, {
        pinned: conv.pinned ? 0 : 1,
      })
    },
    [],
  )

  if (!conversations?.length) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        {t("No conversations yet")}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Search */}
      <div className="border-b px-3 py-2">
        <input
          type="text"
          placeholder={t("Search conversations...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border bg-background px-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
        />
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto">
        {grouped.map((group) => (
          <div key={group.label}>
            <div className="sticky top-0 z-10 bg-background/95 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur-sm">
              {t(group.label)}
            </div>
            {group.items.map((conv) => (
              <button
                key={conv.id}
                onClick={() => onSelect(conv.id!)}
                className={cn(
                  "group flex w-full items-start gap-2 border-b px-4 py-3 text-left transition-colors hover:bg-accent",
                  activeId === conv.id && "bg-accent",
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium">
                    {conv.summaryTitle || conv.title}
                  </span>
                  {conv.preview && (
                    <span className="truncate text-xs text-muted-foreground">
                      {stripMarkdown(conv.preview)}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">
                      {formatRelativeTime(conv.updatedAt)}
                    </span>
                    {conv.category && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] text-muted-foreground">
                        {conv.category}
                      </span>
                    )}
                  </div>
                </div>
                {/* Pin button */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => togglePin(e, conv)}
                  onKeyDown={(e) => { if (e.key === "Enter") togglePin(e as unknown as React.MouseEvent, conv) }}
                  className={cn(
                    "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded transition-colors",
                    conv.pinned
                      ? "text-amber-500"
                      : "text-transparent group-hover:text-muted-foreground/40",
                  )}
                  title={conv.pinned ? t("Unpin") : t("Pin")}
                >
                  <PinIcon pinned={!!conv.pinned} />
                </div>
              </button>
            ))}
          </div>
        ))}
        {filtered.length === 0 && search && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t("No conversations matching \"{{query}}\"", { query: search })}
          </div>
        )}
      </div>
    </div>
  )
})

// ---------------------------------------------------------------------------
// Pin icon
// ---------------------------------------------------------------------------

function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill={pinned ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16h14v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V5a2 2 0 0 0 2-2H7a2 2 0 0 0 2 2z" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Markdown stripping
// ---------------------------------------------------------------------------

function stripMarkdown(text: string): string {
  return text
    .replace(/[*_~`#]+/g, "") // bold, italic, strikethrough, code, headers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1") // images
    .replace(/^\s*[-+]\s+/gm, "") // list markers
    .replace(/\s+/g, " ") // collapse whitespace
    .trim()
}

// ---------------------------------------------------------------------------
// Date grouping
// ---------------------------------------------------------------------------

interface DateGroup {
  label: string
  items: DbChatConversation[]
}

function groupByDate(conversations: DbChatConversation[]): DateGroup[] {
  const groups: DateGroup[] = []
  const pinned: DbChatConversation[] = []
  const today: DbChatConversation[] = []
  const yesterday: DbChatConversation[] = []
  const thisWeek: DbChatConversation[] = []
  const lastWeek: DbChatConversation[] = []
  const thisMonth: DbChatConversation[] = []
  const older: DbChatConversation[] = []

  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
  const startOfThisWeek = new Date(startOfToday.getTime() - startOfToday.getDay() * 86400000)
  const startOfLastWeek = new Date(startOfThisWeek.getTime() - 7 * 86400000)
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1)

  for (const conv of conversations) {
    if (conv.pinned) {
      pinned.push(conv)
      continue
    }
    const date = new Date(conv.updatedAt)
    if (date >= startOfToday) today.push(conv)
    else if (date >= startOfYesterday) yesterday.push(conv)
    else if (date >= startOfThisWeek) thisWeek.push(conv)
    else if (date >= startOfLastWeek) lastWeek.push(conv)
    else if (date >= startOfThisMonth) thisMonth.push(conv)
    else older.push(conv)
  }

  if (pinned.length) groups.push({ label: "Pinned", items: pinned })
  if (today.length) groups.push({ label: "Today", items: today })
  if (yesterday.length) groups.push({ label: "Yesterday", items: yesterday })
  if (thisWeek.length) groups.push({ label: "This week", items: thisWeek })
  if (lastWeek.length) groups.push({ label: "Last week", items: lastWeek })
  if (thisMonth.length) groups.push({ label: "This month", items: thisMonth })
  if (older.length) groups.push({ label: "Older", items: older })

  return groups
}

// ---------------------------------------------------------------------------
// Relative time formatting
// ---------------------------------------------------------------------------

function formatRelativeTime(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)

  if (diffMin < 1) return "Just now"
  if (diffMin < 60) return `${diffMin}m ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString()
}
